import React, { useEffect, useState } from 'react'
import {Form} from 'react-final-form'
import {FormattedHTMLMessage, FormattedMessage} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {Redirect} from 'react-router'
import {IDeviceInfo} from '../../../../models/sub/DeviceInfo'
import {IUser} from '../../../../models/User'
import {loginSuccess} from '../../../actions/session'
import UserPlusSolid from '../../../assets/icons/UserPlusSolid.svg'
import {GaEventAction, GaEventCategory, GaEventLabel, urlPrefixes, UserType} from '../../../config/constants'
import {IState} from '../../../config/store'
import { isNativeWebview } from '../../../helpers/reactNativeWebview'
import {isoFetchNoti} from '../../../helpers/util'
import {useConfirm, useLocale, useQuery, useUserType, useIsSsr} from '../../hocs/hooks'
import styles from '../login/Login.scss'
import Logo from '../login/Logo'
import CancelSignup from './CancelSignup'
import SignupFields from './SignupFields'
import SignupTitle from './SignupTitle'
import SocialButtons, { socialButtonsMessages } from './SocialButtons'
import Terms from './Terms'
import queryString from 'query-string'
import ms from 'ms'

interface ISignupForm {
  agreeContract: boolean
  deviceInfo: IDeviceInfo
}

const Signup = () => {
  const userType = useUserType()
  const user = useSelector(({ session: { user } }: IState) => user)
  const dispatch = useDispatch()
  const {isOk, emailConfirmed, createdAt, updatedAt} = user
  const locale = useLocale()
  const {redirectTo} = useQuery()
  const confirm = useConfirm()
  const isMobileApp = useIsSsr() ? false : isNativeWebview()
  const [enableSignUpEmail, setEnableSignupEmail] = useState<Boolean>(isMobileApp)
  useEffect(() => {
    setEnableSignupEmail(isMobileApp)
  }, [isMobileApp])
  if (isOk) {
    const isNewUser = emailConfirmed && (+createdAt - +updatedAt) === 0 && (+new Date() - +createdAt) <= ms('1 minute')
    const [url, query] = (redirectTo || `/${locale}${urlPrefixes[userType]}`).split('?')
    const newQuery = queryString.stringify({
      ...queryString.parse(query),
      ...(![UserType.admin, UserType.author].includes(userType) && isNewUser)
        ? {gaAction: GaEventLabel.signupSuccess}
        : {}
    })
    return <Redirect to={`${url}${newQuery ? `?${newQuery}` : ''}`} />
  }
  return <Form
      initialValues={{
        ...user,
        agreeContract: false,
        first: user.first || user.name,
        email: user.email
      }}
      onSubmit={async (values: ISignupForm) => {
        const user: IUser = await isoFetchNoti(`/${locale}${urlPrefixes[userType]}/api/signup`, {
          method: 'POST',
          body: values
        })
        if (userType === UserType.user && !user.emailConfirmed) {
          await confirm({
            title: <FormattedMessage id="confirm.title"/>,
            body: <div className="mb-2">
              <FormattedHTMLMessage id="signup.confirmText" defaultMessage="Thank you for registering a new driver. <b>Please check the email sent by Carstay and click the link in the email</b> to complete the registration." />
            </div>
          })
        }
        // trace only user, hosts and holders
        if (![UserType.admin, UserType.author].includes(userType)) {
          window.dataLayer.push({
            event: 'event',
            eventProps: {
              category: GaEventCategory.engagement,
              label: GaEventLabel.signup,
              action: GaEventAction[userType][GaEventLabel.signup].email,
            }
          })
        }
        dispatch(loginSuccess(user))
      }}
    >
      {(
        {
          handleSubmit,
          submitting,
          invalid,
          values: {agreeContract}
        }) => <form onSubmit={handleSubmit} id={`${userType}SignupForm`}>
          <div className={['w-100 d-inline-block', styles.bgLogin].join(' ')}>
            <div className={['ml-auto mr-auto', styles.loginCard, styles.signupCard].join(' ')}>
              <div className={['ml-auto mr-auto', styles.loginPanel].join(' ')}>
                <div className="d-flex flex-column">
                  <Logo/>
                  <hr className={styles.hr1}/>
                  <SignupTitle/>
                  {enableSignUpEmail || user._id
                    ? <SignupFields/>
                    : <button type="button" onClick={() => setEnableSignupEmail(true)}
                        className={['form-group btn btn-primary mb-2', styles.loginField, styles.signupButton].join(' ')}>
                        <FormattedMessage defaultMessage="Signup with email" id="userSignup.signupWithEmail" />
                      </button>
                  }
                  {(enableSignUpEmail || user._id ) && <>
                    <Terms/>
                    <button
                      disabled={submitting || invalid || !agreeContract}
                      type="submit"
                      className={[`btn btn-primary ad-reg-${userType}`, styles.loginField].join(' ')}>
                      <UserPlusSolid className={[styles.loginIcon].join(' ')}/>
                      <FormattedMessage defaultMessage="Sign up" id="userSignup.signupBtnLabel"/>
                    </button>
                    </>
                  }
                  {!isMobileApp && !enableSignUpEmail && !user._id && <SocialButtons label={socialButtonsMessages.signup} />}
                  <CancelSignup />
                </div>
              </div>
            </div>
            {/* Need for hubspot */}
            {userType !== UserType.admin && userType !== UserType.author
            && <input type="text" className="d-none" name={`${userType}_lifecyclestage`} value="salesqualifiedlead" readOnly></input>}
          </div>
        </form>
      }
    </Form>
}
Signup.displayName = 'Signup'
export default Signup
